import type { Value } from 'platejs';

interface TextLeaf {
    text: string;
    bold?: true;
    italic?: true;
    code?: true;
}

function parseInlineMarks(raw: string): TextLeaf[] {
    const leaves: TextLeaf[] = [];
    // Regex handles **bold**, *italic*, and `code` (non-greedy, single pass).
    const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(raw)) !== null) {
        if (match.index > lastIndex) {
            leaves.push({ text: raw.slice(lastIndex, match.index) });
        }
        if (match[2]) leaves.push({ text: match[2], bold: true });
        else if (match[3]) leaves.push({ text: match[3], italic: true });
        else if (match[4]) leaves.push({ text: match[4], code: true });
        lastIndex = regex.lastIndex;
    }

    if (lastIndex < raw.length) {
        leaves.push({ text: raw.slice(lastIndex) });
    }
    if (leaves.length === 0) leaves.push({ text: '' });
    return leaves;
}

export function markdownToPlate(md: string): Value {
    const lines = md.split('\n');
    const nodes: any[] = [];

    for (const line of lines) {
        const trimmed = line.trimEnd();

        // Headings
        const h3 = trimmed.match(/^###\s+(.+)/);
        if (h3) {
            nodes.push({ type: 'h3', children: parseInlineMarks(h3[1]) });
            continue;
        }
        const h2 = trimmed.match(/^##\s+(.+)/);
        if (h2) {
            nodes.push({ type: 'h2', children: parseInlineMarks(h2[1]) });
            continue;
        }
        const h1 = trimmed.match(/^#\s+(.+)/);
        if (h1) {
            nodes.push({ type: 'h1', children: parseInlineMarks(h1[1]) });
            continue;
        }

        // Unordered list items
        const ul = trimmed.match(/^[-*]\s+(.+)/);
        if (ul) {
            nodes.push({
                type: 'p',
                listStyleType: 'disc',
                indent: 1,
                children: parseInlineMarks(ul[1]),
            });
            continue;
        }

        // Ordered list items
        const ol = trimmed.match(/^\d+\.\s+(.+)/);
        if (ol) {
            nodes.push({
                type: 'p',
                listStyleType: 'decimal',
                indent: 1,
                children: parseInlineMarks(ol[1]),
            });
            continue;
        }

        // Blockquote
        const bq = trimmed.match(/^>\s*(.*)/);
        if (bq) {
            nodes.push({ type: 'blockquote', children: parseInlineMarks(bq[1] || '') });
            continue;
        }

        // Horizontal rule
        if (/^---+$/.test(trimmed) || /^\*\*\*+$/.test(trimmed)) {
            nodes.push({ type: 'hr', children: [{ text: '' }] });
            continue;
        }

        // Blank line — skip (don't generate empty paragraphs between blocks).
        if (!trimmed) continue;

        // Default: paragraph
        nodes.push({ type: 'p', children: parseInlineMarks(trimmed) });
    }

    if (nodes.length === 0) {
        nodes.push({ type: 'p', children: [{ text: '' }] });
    }

    return nodes as Value;
}
